import React, { useCallback, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { FolderName, ProgressCallback, SKMediaType, uploadFile, ErrorCallback } from "../../../api/shared/uploadService";
import { UploadTask } from "firebase/storage";
import * as Yup from "yup";
import LoadingPopup from "../../channels/create/LoadingPopup";

interface MultiTypeInVisibleInputProps {
  index: number;
  onFileChange: (index: number, fileUrl: string, fileType: SKMediaType) => void;
  docId: string;
  maxFileSize: number;
  imageUrl: string;
  editable?: boolean;
  folder?: FolderName;
}

// Define a custom ref type
interface MultiTypeInputRef {
  click: () => void;
}

const MultiTypeInVisibleInput = forwardRef<MultiTypeInputRef, MultiTypeInVisibleInputProps>(
  ({ index = 0, onFileChange, docId, maxFileSize, imageUrl, editable, folder }, ref) => {
    const [filePreview, setFilePreview] = useState<string | ArrayBuffer | null>(imageUrl);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [imageTask, setImageTask] = useState<UploadTask | null>(null);
    const [mediaType, setMediaType] = useState<SKMediaType>(SKMediaType.photo);
    const [isLoading, setIsLoading] = useState(false);

    const documentSchema = Yup.object().shape({
      documentFile: Yup.mixed<File>().test("fileSize", `File Size should be below ${maxFileSize} MB`, value => {
        if (!value) return false;
        const fileSize = value.size;
        return fileSize <= maxFileSize * 1024 * 1024;
      }),
    });

    const getAspectRatio = () => {
      if (imagePreview) {
        const img = new Image();
        img.src = imagePreview;
        return img.naturalWidth / img.naturalHeight;
      }
      return 1;
    };

    const getContainerStyle = () => {
      const aspectRatio = getAspectRatio();
      return `h-[${100 / aspectRatio}%]`;
    };

    const successCallback = useCallback(
      (url: string, mType: SKMediaType) => {
        setIsLoading(false);
        onFileChange(index, url, mType);
      },
      [onFileChange, index]
    );

    const progressCallback: ProgressCallback = (progress: number, mediaType: SKMediaType) => {
      console.log(`Upload progress: ${progress}%`);
    };

    const errorCallback: ErrorCallback = (error: Error, mediaType: SKMediaType) => {
      setIsLoading(false);
      console.error(`Upload failed: ${error.message}`);
    };

    const handleFileChange = useCallback(
      async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        try {
          const fileUrl = URL.createObjectURL(file);
          await documentSchema.validate({ documentFile: file }, { abortEarly: false });
          setErrorMessage("");
          setFilePreview(fileUrl);
          let task = uploadFile(
            file,
            docId,
            file.type.startsWith("image/") ? SKMediaType.photo : SKMediaType.video,
            progressCallback,
            errorCallback,
            successCallback,
            folder
          );
          setImageTask(task);
          setIsLoading(true);
        } catch (err) {
          let error = err as Error;
          setErrorMessage(error.message);
          setIsLoading(true);
        }
      },
      [successCallback, docId, documentSchema]
    );

    useImperativeHandle(ref, () => ({
      click: () => {
        fileInputRef.current?.click();
      },
    }));

    return (
      <div id="mediaContainer" className="flex flex-col">
        <div
          onClick={() => {
            editable && fileInputRef?.current?.click();
          }}
        >
          <input
            key={index}
            name="imageFile"
            type="file"
            ref={fileInputRef}
            accept="image/*,video/*"
            onChange={handleFileChange}
            className="hidden"
            id={`file-upload-${index}`}
            disabled={!editable}
          />

          <label htmlFor="file-upload" className="cursor-pointer content-center h-[114px] md:h-[220px]">
            <div id="showMessage" className="flex flex-col items-center">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center flex-shrink-0 p-4 mb-5 space-y-4 bg-gray-100 shadow-sm rounded-2xl">
                  <img src={"/icons/mediaupload.gif"} alt="Saving" className="w-16 h-16" />
                  <div className="font-bold text-center text-gray-500">Uploading...</div>
                  <button
                    className="px-4 py-2 text-black bg-white border border-gray-400 rounded"
                    onClick={e => {
                      e.stopPropagation();
                      setImagePreview(null);
                      setUploadingPhoto(false);
                      imageTask && imageTask.cancel();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                ""
              )}
            </div>
          </label>
        </div>
        {errorMessage && <p className="mt-2 text-sm text-red-500">{errorMessage}</p>}
      </div>
    );
  }
);

MultiTypeInVisibleInput.displayName = "MultiTypeInVisibleInput";
export default MultiTypeInVisibleInput;
